/**
 * CoachOverviewSection — surfaces all team-level data the v7.6 coach mockup
 * requires: team PDS, rank, total players, avg wellness, training compliance,
 * next match, squad overview table, PDS trend, pending actions, wellness
 * distribution donut, training week, upcoming fixtures, load mgmt, injuries,
 * fundraising overview.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AreaChart, Area, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import api from '@/utils/api';
import {
  TrendingUp, Users, Heart, ClipboardCheck, Calendar, Activity,
  AlertTriangle, Trophy, Loader2, MapPin,
} from 'lucide-react';

const Pill = ({ children, color = 'text-zinc-400 border-zinc-700' }) => (
  <span className={`text-[10px] uppercase tracking-wider font-bold border rounded-full px-2 py-0.5 ${color}`}>{children}</span>
);

function StatCard({ icon: Icon, label, value, sub, accent = '#A3FF00' }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5" data-testid={`coach-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-zinc-400 text-xs uppercase tracking-wider">{label}</p>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="text-2xl font-heading font-bold" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-zinc-500 text-xs mt-1">{sub}</p>}
    </Card>
  );
}

const WELLNESS_COLORS = ['#A3FF00', '#facc15', '#ef4444'];

export default function CoachOverviewSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get('/dashboard/coach-summary')
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <Card className="bg-zinc-900 border-zinc-800 p-12 text-center mb-6"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" /></Card>;
  }
  if (!data) return null;
  const {
    team_pds, team_rank_pct, total_players, squad_capacity, avg_wellness,
    training_compliance_pct, next_match, squad_overview, pds_trend, pending_actions,
    wellness_distribution, load_management, injuries,
    training_week, upcoming_fixtures,
  } = data;

  return (
    <div className="space-y-6 mb-8" data-testid="coach-overview">
      {/* QUICK ACCESS — Performance Hub */}
      <Card className="bg-gradient-to-br from-[#0f1607] via-zinc-900 to-zinc-900 border-[#A3FF00]/30 p-5 flex flex-col md:flex-row items-center gap-4" data-testid="coach-perf-hub-card">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[#A3FF00] text-[10px] font-bold tracking-[0.2em] uppercase">XV VISION · GPS · WEARABLES</span>
            <span className="text-[10px] bg-[#A3FF00]/15 text-[#A3FF00] px-1.5 py-0.5 rounded font-bold">LIVE</span>
          </div>
          <h3 className="text-xl font-heading font-bold text-white">Performance Hub</h3>
          <p className="text-zinc-400 text-sm">Upload match footage for AI analysis. Review squad GPS sessions and weekly training load.</p>
        </div>
        <a href="/performance" className="inline-flex items-center gap-2 bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-5 py-2.5 rounded-md transition-colors" data-testid="coach-open-performance-hub">
          Open Performance Hub →
        </a>
      </Card>

      {/* TOP STRIP */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Trophy} label="Team PDS" value={team_pds || '—'} sub={team_pds ? 'Provisional' : 'Awaiting squad'} />
        <StatCard icon={TrendingUp} label="Team Rank" value={team_rank_pct} sub="Among schools" />
        <StatCard icon={Users} label="Players" value={`${total_players}/${squad_capacity || total_players}`} sub="Full squad" />
        <StatCard icon={Heart} label="Avg Wellness" value={`${avg_wellness}%`} accent="#ef4444" sub={avg_wellness >= 80 ? 'Good' : 'Monitor'} />
        <StatCard icon={ClipboardCheck} label="Training Compliance" value={`${training_compliance_pct}%`} accent="#3b82f6" sub="Good" />
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-zinc-400 text-xs uppercase tracking-wider">Next Match</p>
            <Calendar className="w-4 h-4 text-[#A3FF00]" />
          </div>
          <p className="text-sm font-bold text-white leading-tight">vs {next_match.opponent}</p>
          <p className="text-zinc-500 text-xs mt-1">{next_match.date} · {next_match.time}</p>
          <p className="text-zinc-600 text-[11px] mt-1"><MapPin className="w-3 h-3 inline mr-1" />{next_match.venue}</p>
        </Card>
      </div>

      {/* SQUAD TABLE + PDS TREND + PENDING ACTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr_1fr] gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5 overflow-hidden" data-testid="squad-overview-table">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-bold text-white">Squad Overview <span className="text-zinc-500 font-normal text-sm">({squad_overview.length} players)</span></h3>
          </div>
          {squad_overview.length === 0 ? (
            <div className="py-10 text-center text-zinc-500 text-sm">
              <Users className="w-10 h-10 mx-auto mb-2 text-zinc-700" />
              No players linked yet — invite players from My Team.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
                  <tr className="border-b border-zinc-800">
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Player</th>
                    <th className="text-left py-2">Position</th>
                    <th className="text-left py-2">PDS</th>
                    <th className="text-left py-2">Wellness</th>
                    <th className="text-left py-2">Load</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {squad_overview.map((p) => (
                    <tr key={p.n} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <td className="py-2 text-zinc-500">{p.n}</td>
                      <td className="py-2 text-white font-semibold">{p.player}</td>
                      <td className="py-2 text-zinc-400">{p.position}</td>
                      <td className="py-2"><span className="text-[#A3FF00] font-mono">{p.pds}</span></td>
                      <td className="py-2 text-zinc-300 font-mono">{p.wellness}%</td>
                      <td className="py-2">
                        <Badge className={
                          p.load_label === 'High' ? 'bg-red-500/10 text-red-300 border border-red-500/30 text-[10px]' :
                          p.load_label === 'Monitor' ? 'bg-yellow-500/10 text-yellow-300 border border-yellow-500/30 text-[10px]' :
                          'bg-[#A3FF00]/10 text-[#A3FF00] border border-[#A3FF00]/30 text-[10px]'
                        }>{p.load_label} ({p.training_load})</Badge>
                      </td>
                      <td className="py-2"><Badge className="bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30 text-[10px]">{p.status}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white">PDS Trend</h3>
            <Pill>Provisional</Pill>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={pds_trend}>
              <defs>
                <linearGradient id="cpdsg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A3FF00" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#A3FF00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} domain={[0, 100]} />
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
              <Area type="monotone" dataKey="pds" stroke="#A3FF00" fillOpacity={1} fill="url(#cpdsg)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3">Pending Actions ({pending_actions.filter(a => a.count > 0).length})</h3>
          <div className="space-y-2">
            {pending_actions.map((a, i) => (
              <div key={a.label} className="flex items-start gap-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`pending-action-${i}`}>
                <div className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center shrink-0 text-xs font-bold text-zinc-300">{i + 1}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{a.label}</p>
                  <p className="text-zinc-500 text-[11px] truncate">{a.sub}</p>
                </div>
                <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300 hover:text-[#A3FF00] hover:border-[#A3FF00] h-7 text-[11px] px-3">{a.action}</Button>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* TRAINING WEEK + WELLNESS DISTRIBUTION + FUNDRAISING */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
          <h3 className="font-heading font-bold text-white mb-3">Training This Week</h3>
          <div className="grid grid-cols-6 gap-2">
            {training_week.map((d) => (
              <div key={d.day} className={`text-center rounded-lg p-3 border ${d.highlight ? 'border-[#A3FF00]/40 bg-[#A3FF00]/5' : 'border-zinc-800 bg-zinc-950'}`}>
                <p className="text-[11px] text-zinc-500 font-bold">{d.day}</p>
                <p className="text-[11px] text-zinc-400">{d.date}</p>
                <p className={`text-xs font-bold mt-2 ${d.highlight ? 'text-[#A3FF00]' : 'text-zinc-300'}`}>{d.activity}</p>
                <p className="text-[11px] text-zinc-500 mt-1">{d.time}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-zinc-800">
            <p className="text-xs text-zinc-500 mb-1">Training Compliance</p>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className="h-full bg-[#A3FF00]" style={{ width: `${training_compliance_pct}%` }} />
              </div>
              <span className="text-[#A3FF00] font-bold text-sm">{training_compliance_pct}%</span>
            </div>
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3">Squad Wellness</h3>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={wellness_distribution} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                {wellness_distribution.map((_, i) => (<Cell key={i} fill={WELLNESS_COLORS[i]} />))}
              </Pie>
              <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 text-xs mt-2">
            {wellness_distribution.map((w, i) => (
              <div key={w.name} className="flex justify-between items-center">
                <span className="flex items-center gap-2 text-zinc-400">
                  <span className="w-2 h-2 rounded-full" style={{ background: WELLNESS_COLORS[i] }} />{w.name}
                </span>
                <span className="text-zinc-300 font-mono">{w.value}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* FIXTURES + LOAD + INJURIES + FUNDRAISING */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2">
          <h3 className="font-heading font-bold text-white mb-3">Upcoming Fixtures</h3>
          <div className="space-y-2">
            {upcoming_fixtures.map((f, i) => (
              <div key={`${f.date}-${f.opponent}`} className="flex items-center gap-3 bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`fixture-${i}`}>
                <Calendar className="w-4 h-4 text-[#A3FF00]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{f.opponent}</p>
                  <p className="text-zinc-500 text-xs">{f.date}</p>
                </div>
                <Badge className={f.type === 'Home' ? 'bg-[#A3FF00]/10 text-[#A3FF00] border-[#A3FF00]/30 text-[10px]' : 'bg-blue-500/10 text-blue-300 border-blue-500/30 text-[10px]'}>{f.type}</Badge>
                <span className="text-zinc-400 font-mono text-xs">{f.time}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-bold text-white">Load Mgmt</h3>
            <Pill>Provisional</Pill>
          </div>
          <p className="text-xs text-zinc-500 mb-2">Acute : Chronic Ratio</p>
          <p className="text-3xl font-bold text-[#A3FF00]">{load_management.acwr}</p>
          <Badge className="mt-2 bg-[#A3FF00]/10 text-[#A3FF00] border border-[#A3FF00]/30">{load_management.rating}</Badge>
          <div className="mt-4 text-[11px] text-zinc-500">
            <div className="flex justify-between"><span>Low</span><span>Optimal</span><span>Monitor</span><span>High</span></div>
            <div className="grid grid-cols-4 gap-1 mt-1">
              <div className="h-1.5 bg-zinc-700 rounded-full" />
              <div className="h-1.5 bg-[#A3FF00] rounded-full" />
              <div className="h-1.5 bg-yellow-400/60 rounded-full" />
              <div className="h-1.5 bg-red-500/60 rounded-full" />
            </div>
          </div>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><Trophy className="w-4 h-4 text-[#A3FF00]" />Season Snapshot</h3>
          <p className="text-2xl font-bold text-[#A3FF00]">{total_players}</p>
          <p className="text-zinc-500 text-xs">Players in squad</p>
          <div className="grid grid-cols-2 gap-2 mt-4 text-center">
            <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
              <p className="text-zinc-500 text-[10px]">Team PDS</p>
              <p className="text-sm font-bold text-white">{team_pds}</p>
            </div>
            <div className="bg-zinc-950 border border-zinc-800 rounded p-2">
              <p className="text-zinc-500 text-[10px]">Avg Wellness</p>
              <p className="text-sm font-bold text-white">{avg_wellness}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* INJURIES */}
      {injuries.length > 0 && (
        <Card className="bg-zinc-900 border-zinc-800 p-5">
          <h3 className="font-heading font-bold text-white mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-yellow-400" />Injuries &amp; Management</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {injuries.map((inj, i) => (
              <div key={`${inj.player}-${inj.issue}`} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3" data-testid={`injury-${i}`}>
                <p className="font-semibold text-white text-sm">{inj.player}</p>
                <p className="text-zinc-400 text-xs">{inj.issue}</p>
                <div className="flex items-center justify-between mt-2">
                  <Badge className="bg-zinc-800 text-zinc-300 text-[10px]">{inj.stage}</Badge>
                  <span className="text-zinc-500 text-[11px]">Return: {inj.return_date}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
